// «Suelta aquí el PDF del mes»: one PDF with every payslip of a month is split in the browser,
// each page is matched to its person by the DNI printed on it, the result is reviewed, and every
// payslip then goes through the same path as a single upload (/upload, payroll history, notice).
// Each payslip's amounts (coste empresa, bruto, líquido) are read too and can be corrected in the
// review. The whole PDF never reaches the server. Libraries: pdf.js (read text) and pdf-lib (split),
// served by SICAF and loaded only here.
// A PDF of settlements (finiquitos) goes the same way: each one is attached to that person's payslip
// of the month of the end date (the payslip must be uploaded first).
const NominasBatch = {
    MONTHS: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
    DNI_LETTERS: 'TRWAGMYFPDXBNJZSQVHLCKE',
    _tools: null,
    _state: null,

    /** pdf.js and pdf-lib, loaded the first time they are needed. */
    tools() {
        if (!this._tools) {
            this._tools = (async () => {
                const pdfjs = await import('/vendor/pdfjs.mjs');
                pdfjs.GlobalWorkerOptions.workerSrc = '/vendor/pdfjs.worker.mjs';
                if (!window.PDFLib) {
                    await new Promise((resolve, reject) => {
                        const script = document.createElement('script');
                        script.src = '/vendor/pdf-lib.js';
                        script.onload = resolve;
                        script.onerror = () => reject(new Error('No se pudo cargar el lector de PDF.'));
                        document.head.append(script);
                    });
                }
                return { pdfjs, PDFLib: window.PDFLib };
            })();
            this._tools.catch(() => { this._tools = null; });
        }
        return this._tools;
    },

    /** Frees a document read with pdf.js (since pdf.js 6 only its loading task has destroy()). */
    close(pdf) {
        pdf?.loadingTask?.destroy?.();
    },

    /**
     * A payslip with its settlement (finiquito) pages after it, as one PDF (a blob URL the
     * caller revokes), so «Ver» and «Descargar» show both. Null when the payslip is no PDF
     * (a scanned image) or has no settlement.
     */
    async payslipWithSettlement(item) {
        if (!item?.filePath || !item.settlementFilePath) return null;
        const file = path => PrivateDownloads.fetchFile('/' + String(path).replace(/^\//, '')).then(({ blob }) => blob.arrayBuffer());
        const [payslip, settlement] = await Promise.all([file(item.filePath), file(item.settlementFilePath)]);
        const { PDFLib } = await this.tools();
        const isPdf = bytes => new TextDecoder().decode(new Uint8Array(bytes, 0, 5)) === '%PDF-';
        if (!isPdf(payslip) || !isPdf(settlement)) return null;
        const joined = await PDFLib.PDFDocument.create();
        for (const bytes of [payslip, settlement]) {
            const source = await PDFLib.PDFDocument.load(bytes);
            for (const page of await joined.copyPages(source, source.getPageIndices())) joined.addPage(page);
        }
        return URL.createObjectURL(new Blob([await joined.save()], { type: 'application/pdf' }));
    },

    /** «nomina.pdf» → «nomina + finiquito.pdf». */
    joinedName(item) {
        return String(item.fileName || 'Nomina.pdf').replace(/(\.pdf)?$/i, ' + finiquito.pdf');
    },

    normalizeDni(value) {
        return String(value || '').toUpperCase().replace(/[^0-9A-Z]/g, '');
    },

    /** Spanish DNI/NIE with a correct control letter (company codes and other numbers are ignored). */
    validDni(dni) {
        const m = /^([0-9XYZ])(\d{7})([A-Z])$/.exec(dni);
        if (!m) return false;
        const number = Number(({ X: '0', Y: '1', Z: '2' }[m[1]] ?? m[1]) + m[2]);
        return this.DNI_LETTERS[number % 23] === m[3];
    },

    /** Valid DNIs/NIEs in a page's text, also when written with dots, spaces or a dash. */
    findDnis(text) {
        const found = new Set();
        for (const match of String(text).toUpperCase().matchAll(/\b[0-9XYZ](?:[ .]?\d){7}[ -]?[A-Z]\b/g)) {
            const dni = this.normalizeDni(match[0]);
            if (this.validDni(dni)) found.add(dni);
        }
        return [...found];
    },

    professorsByDni() {
        const map = new Map();
        for (const p of DataManager.getProfessors() || []) {
            const dni = this.normalizeDni(p.dni);
            if (this.validDni(dni)) map.set(dni, p);
        }
        return map;
    },

    /**
     * The worker's data as Spanish payslips usually print it: under «TRABAJADOR» the name as
     * «APELLIDOS, NOMBRE» (left of «D.N.I.») and the Social Security number (under
     * «AFILIACIÓN»); under «F.ALTA / ANTIGÜEDAD» the hire date. Best effort: every field is
     * shown in a form to check before creating anyone.
     */
    readWorker(items) {
        const cells = items.filter(i => i.str && i.str.trim()).map(i => ({ text: i.str.trim(), x: i.transform[4], y: i.transform[5] }));
        const label = re => cells.find(c => re.test(c.text));
        const below = (row, from = -Infinity, to = Infinity) => cells.filter(c => c.y < row.y - 3 && c.y > row.y - 18 && c.x >= from && c.x < to).sort((a, b) => a.x - b.x);
        const worker = {};
        const head = label(/^TRABAJADOR/i), dniLabel = label(/^D\.?\s?N\.?\s?I/i), nafLabel = label(/AFILIACI/i);
        if (head) {
            const name = below(head, head.x - 10, dniLabel ? dniLabel.x - 15 : Infinity).map(c => c.text).join(' ').replace(/\s+/g, ' ').trim();
            const titled = text => text.toLowerCase().replace(/(^|[\s-])(\p{L})/gu, (m, sep, ch) => sep + ch.toUpperCase())
                .replace(/\s(De|Del|La|Las|Los|Y)(?=\s)/g, word => word.toLowerCase());
            if (name.includes(',')) {
                const [last, first] = name.split(',');
                worker.lastName = titled(last.trim());
                worker.firstName = titled(first.trim());
            } else if (name) {
                worker.lastName = titled(name);
            }
        }
        if (nafLabel) {
            const digits = below(nafLabel, nafLabel.x - 15).map(c => c.text).filter(t => /^[\d\s/-]+$/.test(t)).join(' ').replace(/\s+/g, ' ').trim();
            if (/\d{8,}/.test(digits.replace(/\D/g, ''))) worker.ssNumber = digits;
        }
        const antig = label(/^ANTIG/i);
        if (antig) {
            const alta = cells.find(c => Math.abs(c.y - antig.y) < 3 && /ALTA/i.test(c.text));
            const dates = below(antig).filter(c => /^\d{2}[-/.]\d{2}[-/.]\d{2,4}$/.test(c.text));
            const pick = alta ? dates.sort((a, b) => Math.abs(a.x - alta.x) - Math.abs(b.x - alta.x))[0] : dates[0];
            if (pick) {
                const [d, m, y] = pick.text.split(/[-/.]/);
                const year = y.length === 4 ? y : (Number(y) > 50 ? '19' : '20') + y;
                const iso = `${year}-${m}-${d}`;
                if (!Number.isNaN(new Date(`${iso}T12:00:00`).getTime())) worker.hireDate = iso;
            }
        }
        return worker;
    },

    /**
     * The payslip totals, read from under their labels as Spanish payslips print them:
     * «COSTE EMPRESA» (what the school pays), «T.DEVENGO» / «TOTAL DEVENGADO» (gross) and
     * «LÍQUIDO TOTAL A PERCIBIR» (net). An amount not found is null, to be typed in the review.
     */
    readAmounts(items) {
        const cells = items.filter(i => i.str && i.str.trim()).map(i => ({ text: i.str.trim(), x: i.transform[4], y: i.transform[5], w: i.width || 0 }));
        const money = /^\d{1,3}(?:\.\d{3})*,\d{2}$|^\d+,\d{2}$/;
        // A label comes as one piece («COSTE EMPRESA») or as two on the same line.
        const label = (first, second) => cells.find(c => first.test(c.text) && (!second || second.test(c.text.replace(first, '').trim()) ||
            cells.some(n => second.test(n.text) && Math.abs(n.y - c.y) < 3 && n.x > c.x && n.x - c.x < 80)));
        // The amount just below the label, centred within its column.
        const below = (l, reach) => {
            if (!l) return null;
            const hit = cells.filter(c => money.test(c.text) && c.y < l.y - 2 && c.y > l.y - 30)
                .filter(c => c.x + c.w / 2 >= l.x - 15 && c.x + c.w / 2 <= l.x + reach)
                .sort((a, b) => (l.y - a.y) - (l.y - b.y) || Math.abs(a.x - l.x) - Math.abs(b.x - l.x))[0];
            return hit ? Number(hit.text.replace(/\./g, '').replace(',', '.')) : null;
        };
        return {
            companyCost: below(label(/^COSTE\b/i, /^EMPRESA/i), 75),
            grossPay: below(label(/^(T\.?\s?DEVENGO|TOTAL\s+DEVENGADO)/i), 60),
            netPay: below(label(/^L[IÍ]QUIDO/i), 110)
        };
    },

    /** A settlement (finiquito) page: «saldado y finiquitado», or the end date with the liquidation. */
    isSettlementText(text) {
        const t = String(text).toUpperCase();
        return /FINIQUITAD/.test(t) || (/FECHA\s+CESE/.test(t) && /LIQUIDACI[OÓ]N/.test(t));
    },

    /**
     * A settlement's data as printed on the same line as its label: «FECHA CESE» (dd/mm/yyyy),
     * «SUMA» (what the school pays) and «LÍQUIDO A PERCIBIR» (net). Not found is null.
     */
    readSettlement(items) {
        const cells = items.filter(i => i.str && i.str.trim()).map(i => ({ text: i.str.trim(), x: i.transform[4], y: i.transform[5] }));
        const money = /^\d{1,3}(?:\.\d{3})*,\d{2}$|^\d+,\d{2}$/;
        const sameRow = (l, test) => l ? cells.filter(c => Math.abs(c.y - l.y) < 4 && c.x > l.x && test.test(c.text)).sort((a, b) => b.x - a.x)[0] : null;
        const amount = l => { const hit = sameRow(l, money); return hit ? Number(hit.text.replace(/\./g, '').replace(',', '.')) : null; };
        const date = sameRow(cells.find(c => /^FECHA$/i.test(c.text) && cells.some(n => /^CESE$/i.test(n.text) && Math.abs(n.y - c.y) < 4)), /^\d{2}\/\d{2}\/\d{4}$/);
        const [d, m, y] = date ? date.text.split('/') : [];
        return {
            settlementDate: date ? `${y}-${m}-${d}` : null,
            settlementCost: amount(cells.find(c => /^SUMA$/i.test(c.text))),
            settlementNet: amount(cells.find(c => /^L[IÍ]QUIDO$/i.test(c.text) && cells.some(n => /^PERCIBIR$/i.test(n.text) && Math.abs(n.y - c.y) < 4)))
        };
    },

    /** The amount keys of what is being reviewed, and the one without which it costs nothing. */
    amountKeys() {
        return this._state?.kind === 'settlement' ? ['settlementCost', 'settlementNet'] : ['companyCost', 'grossPay', 'netPay'];
    },

    costKey() {
        return this._state?.kind === 'settlement' ? 'settlementCost' : 'companyCost';
    },

    /** The amounts of a one-person payslip PDF (each one from the last page that shows it). */
    async readPdfAmounts(bytes) {
        const { pdfjs } = await this.tools();
        const pdf = await pdfjs.getDocument({ data: bytes, isEvalSupported: false }).promise;
        const amounts = { companyCost: null, grossPay: null, netPay: null };
        try {
            for (let n = 1; n <= pdf.numPages; n++) {
                const read = this.readAmounts((await (await pdf.getPage(n)).getTextContent()).items);
                for (const key of Object.keys(amounts)) if (read[key] != null) amounts[key] = read[key];
            }
        } finally { this.close(pdf); }
        return amounts;
    },

    /** A typed amount («2.643,00», «2643.5», empty) as euros, or null. */
    parseAmount(value) {
        const text = String(value ?? '').trim().replace(/\s|€/g, '');
        if (!text) return null;
        const amount = Number(text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text);
        return Number.isFinite(amount) && amount >= 0 ? Math.round(amount * 100) / 100 : null;
    },

    /** A person's amounts: what was typed in the review, else the last page of theirs that shows each one. */
    groupAmounts(professorId) {
        const amounts = Object.fromEntries(this.amountKeys().map(key => [key, null]));
        for (const p of this._state.pages) if (p.professorId === professorId && p.amounts) {
            for (const key of Object.keys(amounts)) if (p.amounts[key] != null) amounts[key] = p.amounts[key];
        }
        return { ...amounts, ...(this._state.amounts?.[professorId] || {}) };
    },

    setAmount(professorId, key, value) {
        if (!this._state) return;
        this._state.amounts = this._state.amounts || {};
        this._state.amounts[professorId] = { ...(this._state.amounts[professorId] || {}), [key]: this.parseAmount(value) };
        // Not redrawn (the next field keeps the focus): only the missing-cost warning follows the value.
        if (key === this.costKey()) document.querySelectorAll('[data-no-cost]').forEach(el => {
            if (el.dataset.noCost === professorId) el.hidden = this._state.amounts[professorId][key] != null;
        });
    },

    /** Two-decimal amount for an input («2643.00»), empty when unknown. */
    amountValue(amount) {
        return amount == null ? '' : Number(amount).toFixed(2);
    },

    canCreateProfessors() {
        return AuthManager.isAdmin() || AuthManager.isRRHH() || AuthManager.hasModule('personal-laboral', 'manage');
    },

    alreadyUploaded(professorId, month, year) {
        return !!this.payslipOf(professorId, month, year);
    },

    payslipOf(professorId, month, year) {
        return (NominasUpload.getHistory() || []).find(h => h.professorId === professorId && Number(h.monthIndex) === Number(month) && String(h.year) === String(year)) || null;
    },

    /** Month and year a settlement belongs to: that of its end date, else the month on screen. */
    settlementMonth(professorId) {
        const page = this._state.pages.find(p => p.professorId === professorId && p.settlementDate);
        if (!page) return { month: this._state.month, year: this._state.year };
        const [y, m] = page.settlementDate.split('-').map(Number);
        return { month: m, year: y };
    },

    /** Reads a dropped PDF for one month and opens the review. Progress goes to `status`. */
    async analyzeFile(file, month, year, status) {
        if (!AuthManager.requireModuleManage('nominas', ['admin', 'rrhh'])) return;
        const say = text => { if (status) status.textContent = text; };
        if (!/pdf$/i.test(file.type) && !/\.pdf$/i.test(file.name)) { Utils.showToast('Suelta un PDF. Una imagen escaneada se sube a mano.', 'error'); return; }
        if (file.size > 50 * 1024 * 1024) { Utils.showToast('El PDF no puede pasar de 50 MB.', 'error'); return; }
        if (this._busy) return;
        this._busy = true;
        try {
            say('Cargando el lector de PDF…');
            const { pdfjs } = await this.tools();
            const bytes = new Uint8Array(await file.arrayBuffer());
            const pdf = await pdfjs.getDocument({ data: bytes.slice(), isEvalSupported: false }).promise;
            const byDni = this.professorsByDni(), pages = [];
            for (let n = 1; n <= pdf.numPages; n++) {
                say(`Leyendo la página ${n} de ${pdf.numPages}…`);
                const content = await (await pdf.getPage(n)).getTextContent();
                const text = content.items.map(item => item.str).join(' ');
                const dnis = this.findDnis(text);
                const worker = this.readWorker(content.items);
                const known = dnis.filter(d => byDni.has(d));
                // One person per page: the DNI that has a ficha, or the only DNI on the page.
                const dni = known.length === 1 ? known[0] : (!known.length && dnis.length === 1 ? dnis[0] : null);
                const settlement = this.isSettlementText(text);
                const read = settlement ? this.readSettlement(content.items) : null;
                pages.push({ page: n, dni, worker, settlement, settlementDate: read?.settlementDate || null,
                    amounts: settlement ? { settlementCost: read.settlementCost, settlementNet: read.settlementNet } : this.readAmounts(content.items),
                    professorId: dni && byDni.has(dni) ? byDni.get(dni).id : '', ambiguous: known.length > 1 });
            }
            // A PDF is payslips or settlements: mixed, each kind goes in its own PDF.
            const withDni = pages.filter(p => p.dni);
            const settlements = withDni.filter(p => p.settlement).length;
            if (settlements && settlements < withDni.length) {
                this.close(pdf);
                say('Este PDF mezcla nóminas y finiquitos. Sube cada cosa en su propio PDF.');
                return;
            }
            const kind = settlements ? 'settlement' : 'payslip';
            // A page with no DNI of its own continues the previous payslip (second page of the same person).
            for (let i = 1; i < pages.length; i++) if (!pages[i].dni && !pages[i].ambiguous && pages[i - 1].professorId) { pages[i].professorId = pages[i - 1].professorId; pages[i].continued = true; }
            this._state = { bytes, pdf, file, kind, month: Number(month), year: Number(year), pages, preview: null, creating: null, amounts: {} };
            say(`${file.name}: ${pdf.numPages} ${pdf.numPages === 1 ? 'página leída' : 'páginas leídas'}. Revísalas en la ventana.`);
            this.renderReview();
        } catch (e) {
            console.error('Payroll batch:', e);
            say('No se pudo leer el PDF. Comprueba que es un PDF con texto (no un escaneo); si es un escaneo, súbelo a mano.');
        } finally {
            this._busy = false;
        }
    },

    /**
     * Payslips to save: pages of the same person, in order, become one PDF each. A settlement
     * is saved only when that person's payslip of its month is already there.
     */
    groups() {
        const groups = new Map();
        for (const p of this._state.pages) {
            if (!p.professorId) continue;
            if (this._state.kind === 'settlement' && !this.settlementPayslip(p.professorId)) continue;
            if (!groups.has(p.professorId)) groups.set(p.professorId, []);
            groups.get(p.professorId).push(p.page);
        }
        return groups;
    },

    /**
     * The contract a settlement closes: the last one that started on or before its end date.
     * The settlement is what really happened, so that contract's termination becomes its date.
     * Returns { contract, change } (change false when it already ends that day), or null.
     */
    settlementContract(professorId, date) {
        if (!date) return null;
        const contract = StaffPay.contractsOf(DataManager.getProfessor(professorId)).filter(c => c.startDate <= date).pop();
        return contract ? { contract, change: contract.endDate !== date } : null;
    },

    /** The payslip a person's settlement attaches to. */
    settlementPayslip(professorId) {
        const { month, year } = this.settlementMonth(professorId);
        return this.payslipOf(professorId, month, year);
    },

    renderReview() {
        const { pages, month, year, file, kind } = this._state;
        const settlement = kind === 'settlement';
        const shortDate = iso => iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
        const professors = (DataManager.getProfessors() || []).slice().sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
        const name = id => { const p = DataManager.getProfessor(id); return p ? `${p.firstName} ${p.lastName}`.trim() : ''; };
        const counts = new Map();
        for (const p of pages) if (p.professorId) counts.set(p.professorId, (counts.get(p.professorId) || 0) + 1);
        const groups = this.groups();
        const rows = pages.map((p, i) => {
            const notes = [];
            if (p.ambiguous) notes.push('<span class="pb-warn">Varios DNI de fichas en la página: elige</span>');
            else if (!p.dni && !p.continued) notes.push('<span class="pb-warn">Sin DNI reconocible: elige</span>');
            else if (p.dni && !p.professorId) notes.push('<span class="pb-warn">DNI sin ficha: elige o déjala fuera</span>');
            if (p.continued) notes.push('<span class="pb-note">Continúa la página anterior</span>');
            const firstOfPerson = p.professorId && !pages.slice(0, i).some(q => q.professorId === p.professorId);
            const amounts = firstOfPerson ? this.groupAmounts(p.professorId) : null;
            if (amounts && amounts[this.costKey()] == null) notes.push(`<span class="pb-warn" data-no-cost="${Utils.sanitizeHTML(p.professorId)}">${settlement ? 'Sin «Suma»: escríbela' : 'Sin coste empresa: escríbelo'}</span>`);
            if (settlement && p.professorId && firstOfPerson) {
                const slot = this.settlementMonth(p.professorId), payslip = this.settlementPayslip(p.professorId);
                const slotName = `${this.MONTHS[slot.month - 1].toLowerCase()} ${slot.year}`;
                if (p.settlementDate) notes.push(`<span class="pb-note">Cese el ${shortDate(p.settlementDate)}</span>`);
                else notes.push('<span class="pb-warn">Sin fecha de cese: va al mes elegido</span>');
                if (!payslip) notes.push(`<span class="pb-warn">Falta su nómina de ${slotName}: súbela antes. Este finiquito no se sube</span>`);
                else if (payslip.settlementFilePath) notes.push(`<span class="pb-warn">Ya tiene finiquito en ${slotName}: se sustituye</span>`);
                else notes.push(`<span class="pb-note">Se une a su nómina de ${slotName}</span>`);
                const fix = this.settlementContract(p.professorId, p.settlementDate);
                if (p.settlementDate && !fix) notes.push(`<span class="pb-warn">No tiene contrato antes del ${shortDate(p.settlementDate)}: añádelo en su ficha</span>`);
                else if (fix?.change) notes.push(`<span class="pb-warn">La baja de su contrato del ${shortDate(fix.contract.startDate)} pasa ${fix.contract.endDate ? `del ${shortDate(fix.contract.endDate)}` : 'de «sin fecha»'} al ${shortDate(p.settlementDate)}</span>`);
                else if (fix) notes.push('<span class="pb-note">Su contrato termina ese día</span>');
            } else if (p.professorId && this.alreadyUploaded(p.professorId, month, year)) notes.push('<span class="pb-warn">Ya tiene nómina este mes: se sustituye</span>');
            if (p.professorId && counts.get(p.professorId) > 1 && !p.continued && !(i > 0 && pages[i - 1].professorId === p.professorId)) notes.push('<span class="pb-warn">Esta persona aparece en más de una nómina</span>');
            const actions = `<span class="pb-actions">
                <button type="button" class="btn btn-sm btn-secondary" aria-expanded="${this._state.preview === i}" ${UIEvents.attr("click", function(event,captured){NominasBatch.togglePreview(Number(captured[0]));}, [i])}><i class="fas fa-eye"></i> ${this._state.preview === i ? 'Ocultar' : 'Ver'}</button>
                ${p.dni && !p.professorId && this.canCreateProfessors() ? `<button type="button" class="btn btn-sm btn-primary" ${UIEvents.attr("click", function(event,captured){NominasBatch.toggleCreate(Number(captured[0]));}, [i])}><i class="fas fa-user-plus"></i> Crear profesor</button>` : ''}
            </span>`;
            const extra = this._state.preview === i
                ? `<tr class="pb-extra"><td colspan="4"><div class="pb-preview"><img id="pb-preview-img" alt="Página ${p.page} del PDF"><span class="pb-note" id="pb-preview-status">Cargando la página…</span></div></td></tr>`
                : this._state.creating === i ? `<tr class="pb-extra"><td colspan="4">${this.renderCreateForm(p, i)}</td></tr>` : '';
            const amountInput = (key, label) => `<label class="pb-amount">${label}<input class="form-input" type="number" min="0" step="0.01" inputmode="decimal"
                value="${this.amountValue(amounts[key])}" aria-label="${label} de la página ${p.page}" ${UIEvents.attr("change", function(event,captured){NominasBatch.setAmount(String(captured[0]),String(captured[1]),this.value);}, [p.professorId, key])}></label>`;
            return `<tr>
                <td class="pb-page">Pág. ${p.page}</td>
                <td>
                    <select class="form-input pb-select" aria-label="Persona de la página ${p.page}" ${UIEvents.attr("change", function(event,captured){NominasBatch.assign(Number(captured[0]),this.value);}, [i])}>
                        <option value="">— Dejar fuera —</option>
                        ${professors.map(prof => `<option value="${Utils.sanitizeHTML(prof.id)}" ${prof.id === p.professorId ? 'selected' : ''}>${Utils.sanitizeHTML(`${prof.firstName} ${prof.lastName}`.trim())}</option>`).join('')}
                    </select>
                </td>
                <td class="pb-state">${p.professorId ? `<i class="fas fa-circle-check pb-ok" aria-hidden="true"></i> ${Utils.sanitizeHTML(name(p.professorId))}` : '<i class="fas fa-circle-minus pb-skip" aria-hidden="true"></i> No se sube'} ${notes.join(' ')}${actions}</td>
                <td class="pb-amounts">${amounts ? (settlement ? amountInput('settlementCost', 'Coste (Suma)') + amountInput('settlementNet', 'Líquido') : amountInput('companyCost', 'Coste empresa') + amountInput('grossPay', 'Bruto')) : ''}</td>
            </tr>${extra}`;
        }).join('');
        const scroll = document.querySelector('.pb-table-wrap')?.scrollTop || 0;
        const noun = settlement ? (groups.size === 1 ? 'finiquito' : 'finiquitos') : (groups.size === 1 ? 'nómina' : 'nóminas');
        ModalManager.show(settlement ? 'Revisar finiquitos' : `Revisar nóminas · ${this.MONTHS[month - 1]} ${year}`, `
            <div class="dnr-proposal professor-form-dnr">
                <p class="pf-hint"><strong>${Utils.sanitizeHTML(file.name)}</strong>: ${pages.length} ${pages.length === 1 ? 'página' : 'páginas'} → <strong>${groups.size}</strong> ${noun} ${settlement ? 'para subir' : 'reconocidas'}. Revisa cada página; las que dejes fuera no se suben. ${settlement
                    ? 'Cada finiquito se une a la nómina de esa persona del mes de su fecha de cese, y la baja de su contrato pasa a ser esa fecha. La «Suma» cuenta como coste empresa; corrígela aquí si hace falta.'
                    : 'El coste empresa y el bruto se leen de cada nómina: corrígelos aquí si hace falta.'}</p>
                <div class="pb-table-wrap"><table class="pb-table"><thead><tr><th>Página</th><th>Persona</th><th>Estado</th><th>Importes (€)</th></tr></thead><tbody>${rows}</tbody></table></div>
                <p class="pf-hint" id="batch-progress" role="status"></p>
                <div class="form-actions">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){ModalManager.closeModal();}, [])}>Cancelar</button>
                    <button type="button" class="btn btn-primary" id="batch-upload" ${groups.size ? '' : 'disabled'} ${UIEvents.attr("click", function(event,captured){NominasBatch.upload(this);}, [])}><i class="fas fa-upload"></i> Subir y notificar (${groups.size})</button>
                </div>
            </div>`);
        // Redrawing must not send the reader back to the top of the list.
        const wrap = document.querySelector('.pb-table-wrap');
        if (wrap) wrap.scrollTop = scroll;
        if (this._state.preview !== null) this.renderPreview(this._state.pages[this._state.preview].page);
    },

    togglePreview(index) {
        this._state.preview = this._state.preview === index ? null : index;
        this.renderReview();
    },

    /** The page as an image, drawn in the browser (nothing is sent anywhere). */
    async renderPreview(pageNumber) {
        try {
            const page = await this._state.pdf.getPage(pageNumber);
            const viewport = page.getViewport({ scale: 1.4 });
            const canvas = document.createElement('canvas');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
            const img = document.getElementById('pb-preview-img');
            if (!img) return;
            img.src = canvas.toDataURL('image/png');
            document.getElementById('pb-preview-status')?.remove();
        } catch (e) {
            const status = document.getElementById('pb-preview-status');
            if (status) status.textContent = 'No se pudo mostrar la página.';
        }
    },

    toggleCreate(index) {
        this._state.creating = this._state.creating === index ? null : index;
        this.renderReview();
    },

    renderCreateForm(page, index) {
        const w = page.worker || {}, value = v => Utils.sanitizeHTML(v || '');
        return `<form class="pb-create" id="pb-create-form" ${UIEvents.attr("submit", function(event,captured){event.preventDefault();NominasBatch.createProfessor(Number(captured[0]),this);}, [index])}>
            <p class="pf-hint">Datos leídos de la página ${page.page}. Revísalos: se crea la ficha y esta nómina queda asignada a ella.</p>
            <div class="pb-create-grid">
                <label>Nombre<input class="form-input" name="firstName" required maxlength="120" value="${value(w.firstName)}"></label>
                <label>Apellidos<input class="form-input" name="lastName" required maxlength="200" value="${value(w.lastName)}"></label>
                <label>DNI<input class="form-input" name="dni" required maxlength="40" value="${value(page.dni)}" readonly></label>
                <label>Nº Seguridad Social<input class="form-input" name="ssNumber" maxlength="60" value="${value(w.ssNumber)}"></label>
                <label>Fecha de alta<input class="form-input" type="date" name="hireDate" value="${value(w.hireDate)}"></label>
            </div>
            <div class="pb-create-actions">
                <button type="button" class="btn btn-sm btn-secondary" ${UIEvents.attr("click", function(event,captured){NominasBatch.toggleCreate(Number(captured[0]));}, [index])}>Cancelar</button>
                <button type="submit" class="btn btn-sm btn-primary"><i class="fas fa-user-plus"></i> Crear y asignar</button>
            </div>
        </form>`;
    },

    async createProfessor(index, form) {
        const page = this._state?.pages[index], button = form.querySelector('[type="submit"]');
        if (!page || button.disabled) return;
        button.disabled = true;
        try {
            const created = await DataManager.createProfessor({
                firstName: form.firstName.value.trim(), lastName: form.lastName.value.trim(), dni: page.dni,
                ssNumber: form.ssNumber.value.trim(), hireDate: form.hireDate.value || '', active: true
            });
            if (!created?.id) throw new Error();
            page.professorId = created.id;
            // Its next page without a DNI goes with it, as on reading.
            for (let i = index + 1; i < this._state.pages.length && !this._state.pages[i].dni && !this._state.pages[i].professorId; i++) {
                this._state.pages[i].professorId = created.id;
                this._state.pages[i].continued = true;
            }
            this._state.creating = null;
            Utils.showToast(`Ficha de ${created.firstName} ${created.lastName} creada y nómina asignada.`, 'success');
            this.renderReview();
        } catch (e) {
            Utils.showToast('No se pudo crear la ficha. Revisa los datos y tus permisos.', 'error');
            button.disabled = false;
        }
    },

    assign(index, professorId) {
        const page = this._state?.pages[index];
        if (!page) return;
        page.professorId = professorId;
        page.continued = false;
        this.renderReview();
    },

    /** Splits the reviewed pages into one PDF per person and uploads it (folder nominas). */
    async uploadPdf(source, PDFLib, pageNumbers, filename) {
        const single = await PDFLib.PDFDocument.create();
        for (const copied of await single.copyPages(source, pageNumbers.map(n => n - 1))) single.addPage(copied);
        const blob = new Blob([await single.save()], { type: 'application/pdf' });
        const response = await fetch(`/upload?filename=${encodeURIComponent(filename)}&folder=nominas`, { method: 'POST', headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken() }, body: blob });
        const result = await response.json().catch(() => null);
        if (!response.ok || !result?.success) throw new Error(result?.error || 'Error al subir');
        return result.path;
    },

    /** Settlements: each one is attached to the payslip of its month, then the person is told. */
    async uploadSettlements(button) {
        const progress = document.getElementById('batch-progress');
        const groups = [...this.groups()], saved = [], failed = [];
        try {
            const { PDFLib } = await this.tools();
            const source = await PDFLib.PDFDocument.load(this._state.bytes);
            for (const [index, [professorId, pageNumbers]] of groups.entries()) {
                const professor = DataManager.getProfessor(professorId);
                const name = `${professor.firstName} ${professor.lastName}`.trim();
                const { month, year } = this.settlementMonth(professorId), monthName = this.MONTHS[month - 1];
                if (progress) progress.textContent = `Subiendo ${index + 1} de ${groups.length}: ${name}…`;
                try {
                    const path = await this.uploadPdf(source, PDFLib, pageNumbers, `settlement_${professorId}_${year}_${month}_${Date.now()}.pdf`);
                    const date = this._state.pages.find(p => p.professorId === professorId && p.settlementDate)?.settlementDate || null;
                    saved.push({ professorId, monthIndex: month, year: String(year), monthName, settlementFileName: `Finiquito_${name.replace(/\s+/g, '_')}_${monthName}_${year}.pdf`,
                        settlementFilePath: path, settlementDate: date, ...this.groupAmounts(professorId) });
                } catch (e) {
                    failed.push(name);
                }
            }
            if (saved.length) await NominasUpload.saveSettlements(saved);
            // The settlement is what happened: its contract ends on its date.
            const unfixed = [];
            for (const item of saved) {
                const fix = this.settlementContract(item.professorId, item.settlementDate);
                if (!fix?.change || !fix.contract.id) continue;
                try { await ProfessorService.updateContract(fix.contract.id, { endDate: item.settlementDate }); }
                catch (e) { unfixed.push(DataManager.getProfessor(item.professorId)?.firstName || ''); }
            }
            if (unfixed.length) Utils.showToast(`No se pudo corregir la baja del contrato de ${unfixed.join(', ')}: revísalo en su ficha.`, 'warning');
            for (const item of saved) NominasUpload.notifyUploaded(item.professorId, item.monthName, item.year, true);
            ModalManager.closeModal();
            if (failed.length) Utils.showToast(`Subidos ${saved.length}; fallaron: ${failed.join(', ')}. Vuelve a soltar el PDF para subirlos.`, 'warning');
            else Utils.showToast(`${saved.length} ${saved.length === 1 ? 'finiquito subido' : 'finiquitos subidos'} y notificados.`, 'success');
            if (saved[0]) NominasUpload.uploadMonth = { month: saved[0].monthIndex, year: Number(saved[0].year) };
            NominasUpload.renderSection();
        } catch (e) {
            console.error('Settlement upload:', e);
            Utils.showToast('No se pudo separar el PDF. No se ha subido nada.', 'error');
            button.disabled = false;
        }
    },

    async upload(button) {
        if (!AuthManager.requireModuleManage('nominas', ['admin', 'rrhh']) || button.disabled) return;
        button.disabled = true;
        if (this._state.kind === 'settlement') return this.uploadSettlements(button);
        const { bytes, month, year } = this._state, monthName = this.MONTHS[month - 1], progress = document.getElementById('batch-progress');
        const groups = [...this.groups()], uploaded = [], failed = [];
        try {
            const { PDFLib } = await this.tools();
            const source = await PDFLib.PDFDocument.load(bytes);
            for (const [index, [professorId, pageNumbers]] of groups.entries()) {
                const professor = DataManager.getProfessor(professorId);
                const name = `${professor.firstName} ${professor.lastName}`.trim();
                if (progress) progress.textContent = `Subiendo ${index + 1} de ${groups.length}: ${name}…`;
                try {
                    const path = await this.uploadPdf(source, PDFLib, pageNumbers, `payroll_${professorId}_${year}_${month}_${Date.now()}.pdf`);
                    uploaded.push({ professorId, professorName: name, month: monthName, monthIndex: month, year: String(year),
                        fileName: `Nomina_${name.replace(/\s+/g, '_')}_${monthName}_${year}.pdf`, fileType: 'application/pdf', filePath: path, timestamp: new Date().toISOString(),
                        ...this.groupAmounts(professorId) });
                } catch (e) {
                    failed.push(name);
                }
            }
            // One history save for the whole batch (it syncs the list to the server).
            if (uploaded.length) await NominasUpload.saveUploads(uploaded);
            for (const item of uploaded) NominasUpload.notifyUploaded(item.professorId, monthName, year);
            ModalManager.closeModal();
            if (failed.length) Utils.showToast(`Subidas ${uploaded.length}; fallaron: ${failed.join(', ')}. Súbelas de nuevo una a una.`, 'warning');
            else Utils.showToast(`${uploaded.length} ${uploaded.length === 1 ? 'nómina subida' : 'nóminas subidas'} y notificadas.`, 'success');
            NominasUpload.uploadMonth = { month, year };
            NominasUpload.renderSection();
        } catch (e) {
            console.error('Payroll batch upload:', e);
            Utils.showToast('No se pudo separar el PDF. No se ha subido nada.', 'error');
            button.disabled = false;
        }
    }
};

window.NominasBatch = NominasBatch;
